import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import { type FilterDefinition } from '@oysterjs/ui/search-filter';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseUserFilter,
  getSinceDate
} from '~/api/data/filter-base';
import type { CaraWorkspaceArtifactSummaryFieldsFragment } from '~/api/graphql';

export type DocumentFilter = 'search' | 'user' | 'date';

function artifactMatchesFilter(
  artifact: CaraWorkspaceArtifactSummaryFieldsFragment,
  filter: ActiveFilter<DocumentFilter>
): boolean {
  const agencyUser =
    artifact.metadata && 'agencyUser' in artifact.metadata
      ? (artifact.metadata as { agencyUser?: { id?: string; name?: string } }).agencyUser
      : undefined;
  switch (filter.type) {
    case 'search': {
      const term = (filter.value as string).toLowerCase();
      return (
        artifact.title.toLowerCase().includes(term) ||
        (artifact.metadata.workspaceTitle?.toLowerCase().includes(term) ?? false) ||
        (artifact.metadata.clientName?.toLowerCase().includes(term) ?? false) ||
        (agencyUser?.name?.toLowerCase().includes(term) ?? false)
      );
    }
    case 'user':
      return agencyUser?.id === filter.value;
    case 'date': {
      const since = getSinceDate(filter.value as string);
      if (since == null) return true;
      return new Date(artifact.createdAt) >= since;
    }
    default:
      return true;
  }
}

export function filterDocumentArtifacts(
  artifacts: CaraWorkspaceArtifactSummaryFieldsFragment[],
  activeFilters: ActiveFilter<DocumentFilter>[]
): CaraWorkspaceArtifactSummaryFieldsFragment[] {
  if (activeFilters.length === 0) return artifacts;
  return artifacts.filter((artifact) =>
    activeFilters.every((f) => artifactMatchesFilter(artifact, f))
  );
}

export class DocumentUserFilter extends BaseUserFilter<DocumentFilter> {
  constructor() {
    super('user');
  }
}

export class DocumentDateFilter extends BaseDateFilter<DocumentFilter> {
  constructor() {
    super('date');
  }
}

export class DocumentSearchFilter extends BaseSearchFilter<DocumentFilter> {
  constructor() {
    super('search');
  }
}

export const createDocumentFilterDefinitions = (): FilterDefinition<DocumentFilter>[] => [
  new DocumentUserFilter(),
  new DocumentDateFilter(),
  new DocumentSearchFilter()
];
